/*
===============================================================================================================
                                 Copyright 2022-2025, Blue Yonder Group, Inc.
                                              All Rights Reserved

                                THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
                                            BLUE YONDER GROUP, INC.

                            The copyright notice above does not evidence any actual
                                 or intended publication of such source code.

===============================================================================================================
*/

import React, {
  useContext, useEffect, useRef, useState,
} from 'react';
import Script from 'next/script';
import PropTypes from 'prop-types';
import Magnifier from '@/public/icons/display-card/magnifier.svg';
import addresses from '@/utilities/addresses';
import { LocaleContext } from '@/contexts/locale-context';
import { ThemeContext } from '@/contexts/theme-context';
import { MerchantContext } from '@/contexts/merchant-context';
import TextInput from '../text-input';

/**
 * Wrapper around Loqate address search API widget.
 * Provides UI for address search and autocompletion.
 * @param {function} onLoad Function which is called on Loqate script load
 * @param {string} placeholder Placeholder translation key
 * @param {string} label Label translation key
 * @param {boolean} isAddressRequired Whether or not the input should be validated
 */
export default function AddressAutoComplete({
  onLoad,
  placeholder,
  label,
  isAddressRequired,
}) {
  const { translate } = useContext(LocaleContext);
  const { primaryColor } = useContext(ThemeContext);
  const { getFeature, loqateApiKey } = useContext(MerchantContext);
  const [formattedAddress, setFormattedAddress] = useState('');
  const { addressCountryCode, addressSearchLanguage } = getFeature('addresses');

  const autoCompleteRef = useRef(null);

  const widgetOptions = {
    manualEntryItem: true,
    countries: { codesList: addressCountryCode },
    setCountryByIP: false,
    culture: addressSearchLanguage,
    filteringMode: 'Address',
    maxItems: 10,
    disableTime: 1000,
    list: {
      onlyDown: true,
      force: true,
    },
  };

  const generateAutocompleteFields = () => Object.entries(addresses.autoCompleteMappings).map(
    ([key, field]) => ({
      element: key,
      field,
      mode: window.pca.fieldMode.POPULATE,
    }),
  );

  const fieldsMapping = () => [
    { element: 'auto-complete-input', field: '' },
    ...generateAutocompleteFields(),
  ];

  const handleScriptLoad = () => {
    if (!window.pca) return;
    const addressAutocomplete = new window.pca.Address(fieldsMapping(), {
      key: loqateApiKey,
      ...widgetOptions,
    });

    autoCompleteRef.current = addressAutocomplete;

    addressAutocomplete.listen('display', () => {
      // adding "can't find address" option to suggestions list
      const loqatePopupFooter = document.querySelector('.pcafooteritem');
      const footerChild = loqatePopupFooter.children[0];
      if (loqatePopupFooter && footerChild) {
        const message = translate('details.addressNotFoundOption');
        footerChild.textContent = message;
        footerChild.title = message;
      }
    });
    addressAutocomplete.listen('populate', ({ Label }) => {
      setFormattedAddress(Label.replaceAll('\n', ' '));
    });

    onLoad(addressAutocomplete);
  };

  useEffect(() => {
    if (window.pca) {
      handleScriptLoad();
    }

    return () => {
      autoCompleteRef.current?.destroy();
    };
  }, []);

  return (
    <>
      <div className="auto-complete-input-wrapper">
        <TextInput
          ariaLabel={label}
          inputSelectorId="auto-complete-input"
          formFieldId="auto-complete-input"
          title={label}
          placeholder={placeholder}
          defaultValue={formattedAddress}
          validationRules={{
            ...isAddressRequired && {
              onSubmit: ['addressRequiredButEmpty'],
            },
          }}
        />
        <div className="magnifier">
          <Magnifier />
        </div>
      </div>
      <Script
        id="loqate-js"
        src="https://services.postcodeanywhere.co.uk/js/address-3.91.js"
        // eslint-disable-next-line no-underscore-dangle
        nonce={window.__PORTAL_NONCE}
        onLoad={handleScriptLoad}
      />
      <style jsx>
        {`
          .auto-complete-input-wrapper {
            position: relative;
          }

          .magnifier {
            display: flex;
            align-items: center;
            justify-content: center;
            position: absolute;
            right: 5px;
            top: 35px;
            width: 34px;
            height: 34px;
            background-color: var(--color-white);
          }

          @media (max-width: 768px) {
            .magnifier {
              top: 5px;
            }
          }
        `}
      </style>
      <style global jsx>
        {`
          .pcaautocomplete.pcatext {
            max-width: 98%;
          }

          .pca input {
            box-sizing: border-box;
          }

          .pca.pcalist {
            overflow-y: hidden;
          }

          .pcaitem.pcaitem.pcaitem {
            border-bottom: solid 1px #c7c7cc;
            background-color: var(--color-white);
            font-size: var(--font-size-address);
            font-weight: normal;
            font-stretch: normal;
            font-style: normal;
            line-height: normal;
            letter-spacing: normal;
            color: var(--color-almost-black);
            overflow-x: hidden;
            text-overflow: ellipsis;
            max-width: 100%;
          }

          .pcaitem.pcaitem.pcaitem.pcaselected  {
            background-color: ${primaryColor};
            color: var(--color-white);
          }

          .pcaitem.pcaitem.pcaitem.pcaselected.pcadescription {
            color: var(--color-white);
          }

          .pcaitem.pcaitem.pcafooteritem.pcalastitem {
            color: ${primaryColor};
            text-decoration: underline;
            text-align: center;
          }

          .pcaitem.pcaitem.pcafooteritem.pcalastitem.pcaselected {
            background-color: var(--color-white);
          }

          table.pca.pcaanchor {
            width: 100%;
          }
        `}
      </style>
    </>
  );
}

AddressAutoComplete.defaultProps = {
  onLoad: null,
  placeholder: undefined,
  label: undefined,
  isAddressRequired: false,
};

AddressAutoComplete.propTypes = {
  onLoad: PropTypes.func,
  placeholder: PropTypes.string,
  label: PropTypes.string,
  isAddressRequired: PropTypes.bool,
};
